import { Controller } from '@hotwired/stimulus';
import { EditorState, StateEffect, StateField } from '@codemirror/state';
import { Decoration, EditorView, lineNumbers } from '@codemirror/view';
import { xml } from '@codemirror/lang-xml';
import { HighlightStyle, defaultHighlightStyle, syntaxHighlighting, syntaxTree } from '@codemirror/language';
import { tags as t } from '@lezer/highlight';
import formatXml from 'xml-formatter';

const xmlHighlightStyle = HighlightStyle.define([
  { tag: t.tagName, color: '#0550ae' },
  { tag: t.attributeName, color: '#7c3aed' },
  { tag: t.attributeValue, color: '#1a7f37' },
  { tag: [t.comment, t.blockComment, t.lineComment], color: '#6e7781', fontStyle: 'italic' },
  { tag: t.processingInstruction, color: '#6e7781' },
  { tag: [t.angleBracket, t.bracket, t.punctuation], color: '#57606a' },
  { tag: [t.content, t.string], color: '#1f2328' },
]);

const setLinkedHighlight = StateEffect.define();
const clearLinkedHighlight = StateEffect.define();

const linkedHighlightField = StateField.define({
  create() {
    return Decoration.none;
  },
  update(decorations, tr) {
    let next = decorations.map(tr.changes);
    for (const effect of tr.effects) {
      if (effect.is(clearLinkedHighlight)) {
        next = Decoration.none;
      } else if (effect.is(setLinkedHighlight)) {
        const { from, to } = effect.value;
        next = Decoration.set([Decoration.mark({ class: 'ksef-xml-hl' }).range(from, to)]);
      }
    }
    return next;
  },
  provide: (field) => EditorView.decorations.from(field),
});

const editorTheme = EditorView.theme({
  '&': {
    border: '1px solid #d9dde5',
    borderRadius: '8px',
    minHeight: 'calc(100vh - 280px)',
    overflow: 'hidden',
  },
  '.cm-scroller': {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace',
    fontSize: '13px',
    lineHeight: '1.6',
    overflow: 'auto',
  },
  '.cm-gutters': {
    backgroundColor: '#f7f9fc',
    borderRight: '1px solid #d9dde5',
  },
  '.cm-content': {
    padding: '16px',
  },
  '.ksef-xml-hl': {
    backgroundColor: 'rgba(255, 213, 79, 0.35)',
    outline: '1px solid rgba(255, 167, 38, 0.7)',
    borderRadius: '2px',
  },
});

function getTagName(state, elementNode) {
  const openTag = elementNode.getChild('OpenTag') || elementNode.getChild('SelfClosingTag');
  if (!openTag) return null;
  const tagNameNode = openTag.getChild('TagName');
  if (!tagNameNode) return null;
  const raw = state.doc.sliceString(tagNameNode.from, tagNameNode.to);
  const colon = raw.lastIndexOf(':');
  return colon === -1 ? raw : raw.slice(colon + 1);
}

function findElementRange(state, path) {
  const segments = path.split('/').filter(Boolean);
  if (!segments.length) return null;

  const tree = syntaxTree(state);
  let current = tree.topNode.getChild('Element');
  if (!current) return null;

  for (const segment of segments) {
    let found = null;
    for (const child of current.getChildren('Element')) {
      if (getTagName(state, child) === segment) {
        found = child;
        break;
      }
    }
    if (!found) return null;
    current = found;
  }
  return { from: current.from, to: current.to };
}

export default class extends Controller {
  static targets = ['editor', 'status'];

  static values = {
    url: String,
  };

  connect() {
    this.editorView = null;
    this.handleFieldEnter = this.handleFieldEnter.bind(this);
    this.handleFieldLeave = this.handleFieldLeave.bind(this);
    document.addEventListener('focusin', this.handleFieldEnter);
    document.addEventListener('focusout', this.handleFieldLeave);
    document.addEventListener('mouseover', this.handleFieldEnter);
    document.addEventListener('mouseout', this.handleFieldLeave);
    this.loadPreview();
  }

  disconnect() {
    document.removeEventListener('focusin', this.handleFieldEnter);
    document.removeEventListener('focusout', this.handleFieldLeave);
    document.removeEventListener('mouseover', this.handleFieldEnter);
    document.removeEventListener('mouseout', this.handleFieldLeave);
    this.destroyEditor();
  }

  async loadPreview() {
    if (!this.urlValue) {
      this.showStatus('Brak adresu podglądu XML.');
      return;
    }

    this.showStatus('Ładowanie XML...');

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          Accept: 'text/plain',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const xmlContent = await response.text();

      if (!response.ok) {
        throw new Error(xmlContent || 'Nie udało się pobrać pliku XML z KSeF.');
      }

      this.renderEditor(this.formatXml(xmlContent));
      this.hideStatus();
    } catch (error) {
      this.destroyEditor();
      this.showStatus(error.message || 'Nie udało się pobrać pliku XML z KSeF.');
    }
  }

  renderEditor(xmlContent) {
    this.destroyEditor();

    const state = EditorState.create({
      doc: xmlContent,
      extensions: [
        lineNumbers(),
        xml(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        syntaxHighlighting(xmlHighlightStyle),
        linkedHighlightField,
        EditorState.readOnly.of(true),
        EditorView.editable.of(false),
        editorTheme,
      ],
    });

    this.editorView = new EditorView({
      state,
      parent: this.editorTarget,
    });
  }

  formatXml(xmlContent) {
    try {
      return formatXml(xmlContent, {
        collapseContent: true,
        indentation: '  ',
        lineSeparator: '\n',
      });
    } catch (error) {
      return xmlContent;
    }
  }

  showStatus(message) {
    this.statusTarget.textContent = message;
    this.statusTarget.classList.remove('hide');
  }

  hideStatus() {
    this.statusTarget.classList.add('hide');
  }

  destroyEditor() {
    if (this.editorView) {
      this.editorView.destroy();
      this.editorView = null;
      this.editorTarget.replaceChildren();
    }
  }

  resolveFieldPath(target) {
    if (!target || !target.closest) return null;
    const explicit = target.closest('[data-xml-path]');
    return explicit ? explicit.getAttribute('data-xml-path') : null;
  }

  handleFieldEnter(event) {
    if (!this.editorView) return;
    const path = this.resolveFieldPath(event.target);
    if (!path) return;
    this.highlightPath(path);
  }

  handleFieldLeave(event) {
    if (!this.editorView) return;
    if (!this.resolveFieldPath(event.target)) return;
    const related = event.relatedTarget;
    if (related && this.resolveFieldPath(related)) return;
    this.clearHighlight();
  }

  highlightPath(path) {
    const range = findElementRange(this.editorView.state, path);
    if (!range) return;
    const effects = [setLinkedHighlight.of(range)];
    const visible = this.editorView.visibleRanges.some((r) => r.from <= range.from && range.to <= r.to);
    if (!visible) {
      effects.push(EditorView.scrollIntoView(range.from, { y: 'center' }));
    }
    this.editorView.dispatch({ effects });
  }

  clearHighlight() {
    this.editorView.dispatch({ effects: clearLinkedHighlight.of(null) });
  }
}
